import React, { useCallback, useState } from 'react';
import { IntlShape } from 'react-intl';
import classNames from 'classnames';
import { IDeliveryItemDetailsDTO, IEntityRoleDTO, IEventTrackerDetailsDTO } from '../../../api/events.dao';
import Text from '../text/text';
import CheckIcon from '../../../images/svg/icons8-checkmark-solid';
import { IUserOperatorDTO } from '../../../api/operators.dao';
import './multi-select-with-role.scss';
import { MultiSelectRoleDropdown } from './multi-select-role-dropdown';

export type MultiSelectWithRoleItemsType = IDeliveryItemDetailsDTO | IUserOperatorDTO;

interface IProps extends IEventTrackerDetailsDTO {
    intl: IntlShape;
    keyProperty: string; // selecting items by this key
    intlRoleKey: string; // for localization
    entityRoleKey: string; // property in entity object
    nameProperty: string;
    value: MultiSelectWithRoleItemsType[];
    items: MultiSelectWithRoleItemsType[];
    disabled?: MultiSelectWithRoleItemsType[];
    onChange: (checkedOptions: MultiSelectWithRoleItemsType[]) => void;
    entityRolesList: IEntityRoleDTO[]; // list of the roles
    withUnassigned?: boolean;
    withSearchInput?: boolean;
}

const MultiSelectWithRoleList = (props: IProps) => {
    const {
        intl,
        onChange,
        value,
        items,
        disabled = [],
        keyProperty ,
        entityRoleKey,
        intlRoleKey,
        entityRolesList,
        nameProperty,
        withUnassigned,
        withSearchInput,
    } = props;

    const [selectedValues, setSelectedValues] = useState<MultiSelectWithRoleItemsType[]>(value);
    const [selectedValuesMap, setSelectedValuesMap] = useState(
        selectedValues.reduce((acc, curr) => {
            acc[curr[keyProperty]] = curr;
            return acc;
        }, {})
    );

    const selectValues = values => {
        setSelectedValues(values);
        setSelectedValuesMap(
            values.reduce((acc, curr) => {
                acc[curr[keyProperty]] = curr;
                return acc;
            }, {})
        );
        onChange(values);
    };

    const onCheckBoxClick = (e, selectionItem) => {
        e.stopPropagation();
        if (selectedValuesMap[selectionItem[keyProperty]]) {
            selectValues(selectedValues.filter(teamMember => teamMember[keyProperty] !== selectionItem[keyProperty]));
        } else {
            selectValues([...selectedValues, selectionItem]);
        }
    };

    const setEntityRole = (entityKeyProperty: string, role: string, selectionItem) => {
        let currentMemberIndex = selectedValues.findIndex(item => item[keyProperty] === entityKeyProperty);
        if (currentMemberIndex === -1) {
            currentMemberIndex = selectedValues.length;
            selectValues([...selectedValues, { ...selectionItem, [entityRoleKey]: role }]);
        } else {
            selectValues(
                selectedValues.map((entity, i) => {
                    return i === currentMemberIndex
                        ? {
                              ...entity,
                              [entityRoleKey]: role === entity[entityRoleKey] ? null : role
                          }
                        : entity;
                })
            );
        }
    };

    const entityRolesListWithUnassigned = [
        withUnassigned && {
            id: null,
            label: intl.formatMessage({
                id: 'oe.common.unassigned',
                description: 'Label text',
                defaultMessage: 'Unassigned'
            })
        },
        ...entityRolesList
    ].filter(Boolean);

    const includes = useCallback((list, selectionItem) => list.some(el => el[keyProperty] === selectionItem[keyProperty]), []);

    return (
        <div className='multi-select-list'>
            {items.map((selectionItem: MultiSelectWithRoleItemsType) => {
                const isSelected = !!selectedValuesMap[selectionItem[keyProperty]];
                const isDisabled = includes(disabled, selectionItem);
                const memberRole = selectedValuesMap[selectionItem[keyProperty]]?.[entityRoleKey];
                const memberRoles = memberRole ? entityRolesList : entityRolesListWithUnassigned;

                return (
                    <div
                        className={classNames(
                            'event_form__items-section__item-wrapper multi-select-list__option p-top-0px p-bottom-0px m-left-0px',
                            {
                                'multi-select-list__option--selected': isSelected,
                                'multi-select-list__option--disabled': isDisabled,
                            }
                        )}
                        key={selectionItem[keyProperty]}
                    >
                        <Text className='event_form__items-item-label' message={selectionItem[nameProperty]} withEllipsis />
                        <MultiSelectRoleDropdown
                            withSearchInput={withSearchInput}
                            shouldRenderIcon={!isDisabled}
                            entityRole={memberRole || null}
                            entityRolesList={memberRoles}
                            key={`${selectionItem[keyProperty]}_${memberRoles.length}`}
                            entityRoleKey={selectionItem[keyProperty]}
                            intlEntityRoleKey={intlRoleKey}
                            intl={intl}
                            setEntityRole={!isDisabled ? (entityKeyProperty: string, role: string) => {
                                setEntityRole(entityKeyProperty, role, selectionItem);
                            } : undefined
                            }
                        />

                        <div className={'m-left-10px p-0px'} onClick={!isDisabled ? e => onCheckBoxClick(e, selectionItem) : undefined}>
                            <span className='multi-select-list__checkbox'>
                                <CheckIcon />
                            </span>
                        </div>
                    </div>
                );
            })}
        </div>
    );
};

export { MultiSelectWithRoleList };
